import { useCallback, useRef, useState, useImperativeHandle } from 'react';
import { useRecruitmentFlowAutomations } from '@components/PositionAutomation';
import { useStepAccess } from '@components/RecruitmentFlowEditor/StepAccess/useStepAccess';
import { usePositionFlowPlan } from './usePositionFlowPlan';
import FlowForm from '@components/RecruitmentFlowEditor/FlowForm';
import VideoQuestionsEditor from '@components/VideoQuestionsEditor';
import Skeleton from '@components/Job/components/Skeleton';
import { notification } from '@utils/utils';
import Error from '@components/Job/components/Error';
import { PositionFlowOutcomesSection } from './PositionFlowOutcomesSection';
import { getFlowVideoInterviewQuestionsCount } from './helper';
import './index.less';

const PositionRecruitmentFlow = ({ jobId, onChange, ref, isRecruitmentFlowChanged = false }) => {
    const flowFormRef = useRef(null);
    const [videoStepId, setVideoStepId] = useState(null);

    const {
        flow,
        savedStepIds,
        metadata,
        loading,
        mainLoading,
        error,
        fetchFlowById,
        savePlan,
    } = usePositionFlowPlan(jobId);

    const { renderAutomationsForContext, renderAutomationRuleModal } = useRecruitmentFlowAutomations(jobId, {
        planSteps: flow?.steps ?? [],
        savedStepIds,
        isRecruitmentFlowChanged,
    });

    const { renderStepAccessForStep, warningForRemovingStep, refetchAccess } = useStepAccess(jobId, {
        savedStepIds,
    });

    const renderStepAutomations = useCallback(
        (stepId) => renderAutomationsForContext(stepId),
        [renderAutomationsForContext],
    );

    const renderOutcomeAutomations = useCallback(
        (outcomeId) => renderAutomationsForContext(outcomeId, true),
        [renderAutomationsForContext],
    );

    const _loading = loading || mainLoading;

    const handleVideoSave = useCallback(
        (responseJson) => {
            if (!responseJson || !videoStepId) return;
            // Only update the matching step in FlowForm; never touch parent flow here.
            flowFormRef.current?.mergeVideoInterviewData?.(videoStepId, responseJson);
        },
        [videoStepId],
    );

    const handleSave = useCallback(
        async () => {
            const data = flowFormRef.current.getData();
            const errors = flowFormRef.current.validate();
            if (errors.length > 0) {
                notification(errors.join('\n'), { type: 'error' });
                return false;
            }
            try {
                const result = await savePlan(data);
                if (result?.flow) {
                    flowFormRef.current?.reconcileAfterFlowSave?.(result.flow);
                    // The saved flow may have lost a step, which prunes rules and can end someone's
                    // participation; the server reports that in the response, this reloads the list.
                    refetchAccess();
                }
                return result;
            } catch (err) {
                console.error('Error saving recruitment plan:', err);
                const errorMessage =
                    err.responseJSON?.notification || err.message || 'Failed to save recruitment plan';

                if (err.responseJSON?.notification) {
                    notification(err.responseJSON.notification);
                } else {
                    notification(errorMessage);
                }

                return false;
            }
        },
        [savePlan, refetchAccess],
    );

    useImperativeHandle(
        ref,
        () => ({
            save: async () => await handleSave(),
            fetchFlowById: async (flowId) => await fetchFlowById(flowId),
            getFlow: () => flow,
            getCurrentFlowId: () => flow?.id ?? null,
            getFlowVideoInterviewQuestionsCount: () => {
                const editorData = flowFormRef.current?.getData?.();
                if (editorData?.flow) {
                    return getFlowVideoInterviewQuestionsCount({ steps: editorData.flow });
                }
                return getFlowVideoInterviewQuestionsCount(flow);
            },
            getData: () => flowFormRef.current?.getData(),
        }),
        [handleSave, fetchFlowById, flow],
    );

    if (!jobId) {
        return null;
    }

    return (
        <div className="exclusive-recruitment-flow-editor">
            <Skeleton type={Skeleton.TYPES.RECRUITMENT_FLOW} show={_loading} />
            <Error value={error} show={!loading && error} />
            {!_loading && !error && (
                <>
                    <FlowForm
                        key={flow?.id}
                        ref={flowFormRef}
                        flow={flow}
                        metadata={metadata}
                        onChange={onChange}
                        flowStepVariant="position"
                        allowDragAndDrop={true}
                        extendedRender={renderStepAutomations}
                        renderStepMeta={renderStepAccessForStep}
                        onOpenVideoStepEditor={setVideoStepId}
                        savedStepIds={savedStepIds}
                        extraRemoveWarning={warningForRemovingStep}
                    />
                    <PositionFlowOutcomesSection
                        outcomes={flow?.outcomes || []}
                        extendedRender={renderOutcomeAutomations}
                    />
                    {renderAutomationRuleModal()}
                    {videoStepId && (
                        <VideoQuestionsEditor
                            type="video_interview_questions"
                            jobId={jobId}
                            stepId={videoStepId}
                            showModal={true}
                            onSave={handleVideoSave}
                            onCloseModal={() => setVideoStepId(null)}
                        />
                    )}
                </>
            )}
        </div>
    );
};

export default PositionRecruitmentFlow;
